import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, MoreHorizontal } from 'lucide-react';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useAgreements } from '@/composables/financial/useAgreements';
import { useDebounce } from '@/composables/shared/useDebounce';
import { openPdfInNewTab } from '@/lib/pdfViewer';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Agreement } from '@/types/table';

export function AgreementListPage() {
  const navigate = useNavigate();
  const { agreements, meta, isLoading, filters, sendAgreement, duplicateAgreement, deleteAgreement, downloadPdf } = useAgreements();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Agreement | null>(null);

  const handleDownloadPdf = async (a: Agreement) => {
    try {
      const blob = await downloadPdf(a.id);
      saveAs(blob, `agreement-${a.agreementNumber || a.id}.pdf`);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (a: Agreement) => {
    try {
      await openPdfInNewTab(() => downloadPdf(a.id));
    } catch {
      toast.error('Failed to open PDF');
    }
  };

  const columns: ColumnDef<Agreement, unknown>[] = useMemo(() => [
    { accessorKey: 'agreementNumber', header: ({ column }) => <DataTableColumnHeader column={column} title="Number" />, cell: ({ row }) => <span className="font-medium">{row.original.agreementNumber}</span> },
    { accessorKey: 'kind', header: 'Type', cell: ({ row }) => <Badge variant="outline" className="text-[10px]">{row.original.kind}</Badge> },
    { accessorKey: 'title', header: 'Title', cell: ({ row }) => <span className="truncate max-w-[260px] inline-block">{row.original.title}</span> },
    { accessorKey: 'clientCompanyName', header: 'Client', cell: ({ row }) => row.original.clientCompanyName || row.original.clientRepName || '-' },
    { accessorKey: 'totalAmount', header: ({ column }) => <DataTableColumnHeader column={column} title="Value" />, cell: ({ row }) => row.original.totalAmount != null ? formatCurrency(Number(row.original.totalAmount), row.original.currency) : '-' },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} type="agreement" /> },
    { accessorKey: 'agreementDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />, cell: ({ row }) => formatDate(row.original.agreementDate) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {row.original.status === 'DRAFT' && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); sendAgreement.mutate(row.original.id); }}>Send</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateAgreement.mutate(row.original.id); }}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewPdf(row.original); }}>View PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownloadPdf(row.original); }}>Download PDF</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer
      title="Agreements"
      description="Send and track SDAs, NDAs, and custom agreements"
      actions={<Button onClick={() => navigate('/financial/agreements/new')}><Plus className="mr-2 h-4 w-4" />New Agreement</Button>}
    >
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search by number, title, client..." />
      <DataTable
        columns={columns}
        data={agreements}
        isLoading={isLoading}
        page={meta?.page ?? 1}
        totalPages={meta?.totalPages ?? 1}
        total={meta?.total ?? 0}
        onPageChange={(p) => filters.set({ page: p })}
        onRowClick={(row) => navigate(`/financial/agreements/${row.id}`)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Agreement"
        description={`Delete agreement ${deleteTarget?.agreementNumber}? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteAgreement.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteAgreement.isPending}
      />
    </PageContainer>
  );
}
